import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocalStorage, useNewTranslation } from 'hooks/index'
import { InnerContainer } from 'components/Shared/styles'
import { SubmissionsLayout, SecondHeader, StyledLoading } from './style'
import {
    createGetRequestPagination,
    createGetRequestsAreLoading,
    createGetSubmissionsSummary,
    selectProfile,
    State,
} from 'state'
import {
    clearTeamTypeFilters,
    selectCountryFilter,
    selectTeamFilter,
    selectTeamTypeFilters,
    selectIsHelpdeskSupportedByActiveTeam,
    setTeamTypeFilters,
} from 'state/filter'
import { setSelectedRegistrationView } from 'state/review'
import {
    CampaignsActions,
    SubmissionSummaryState,
    SubmissionsSummaryViews,
    requestSubmissionsSummary,
} from 'state/campaigns'
import { STATE_KEYS } from 'state/requests'
import SubmissionsSummaryCard from './SubmissionsSummaryCard'
import EmptyState from 'components/Shared/EmptyState'
import Text from 'particles/Text'
import Search from 'particles/Search'
import Tab from 'particles/Tab'
import { TeamTypeChipFilter, TeamTypeFilter } from 'components/TeamFilter'
import styled from 'particles/themes/styled'
import { selectIsInternalUser } from 'state/profile'
import Icon from 'components/Icon/Icon'
import { emitShowTooltip } from 'services/tooltipEmitter'

const Submissions: React.FC<any> = ({ submissionsSummary, isLoading, viewType, searchTerm }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const countryFilter = useSelector(selectCountryFilter)
    const teamFilter = useSelector(selectTeamFilter)
    const teamTypeFilters = useSelector(selectTeamTypeFilters)
    const selectedTeamTypeAmount = teamTypeFilters?.submission_list?.selected.length
    const isInternalUser = useSelector(selectIsInternalUser)
    const latestHandleScrollRef = useRef<(dom: HTMLElement) => void>()
    const pagination = useSelector(createGetRequestPagination(STATE_KEYS.SubmissionsSummary))
    const isHelpdeskSupported = useSelector(selectIsHelpdeskSupportedByActiveTeam)
    const profile = useSelector(selectProfile)
    const { hasMore } = pagination
    const [helpdeskTooltipIndicator, setHelpdeskTooltipIndicator] = useLocalStorage<Record<string, string>>(
        'helpdesk-tooltip',
        {},
    )

    const fetchData = (loadMore = false) =>
        dispatch(
            requestSubmissionsSummary({
                loadMore,
                viewType,
                keywords: searchTerm,
                country: countryFilter,
                team_type: teamTypeFilters?.submission_list?.selected,
            }),
        )

    const handleScroll = (dom: HTMLElement) => {
        if (!hasMore) return
        if (!dom || dom.scrollTop === 0) return
        if (dom.scrollTop + window.innerHeight > dom.scrollHeight - 1) {
            fetchData(true)
        }
    }

    useEffect(() => {
        latestHandleScrollRef.current = handleScroll
    }, [hasMore])

    useEffect(() => {
        if (isHelpdeskSupported && !!profile.uuid && !helpdeskTooltipIndicator[profile.uuid]) {
            emitShowTooltip('helpdesk')
            setHelpdeskTooltipIndicator({
                ...helpdeskTooltipIndicator,
                [profile.uuid]: '1',
            })
        }
    }, [isHelpdeskSupported, profile.uuid])

    useEffect(() => {
        fetchData(false)

        return () => {
            dispatch({ type: CampaignsActions.SUBMISSIONS_SUMMARY_CLEAR })
        }
    }, [viewType, dispatch, searchTerm, countryFilter, teamFilter, selectedTeamTypeAmount])

    useEffect(() => {
        const dom = window.document.getElementById('inner-content')
        dispatch(setSelectedRegistrationView(null))
        if (!dom) return
        const onScroll = (dom: HTMLElement) => {
            if (latestHandleScrollRef.current) {
                latestHandleScrollRef.current(dom)
            }
        }
        dom.addEventListener('scroll', () => onScroll(dom))
        return () => dom.removeEventListener('scroll', () => onScroll(dom))
    }, [])

    const shouldDisplay = submissionsSummary.length > 0

    const emptyStateValue = (() => {
        if (selectedTeamTypeAmount > 0) {
            return {
                title: t('submission.review.filterNoResult.title'),
                body: t('submission.review.filterNoResult.subtext'),
                icon: <Icon.EmptyFilterStateIcon />,
            }
        }
        if (viewType === 'active') {
            return {
                title: t('emptyState.noLiveCampaignsTitle'),
                body: t('emptyState.noLiveCampaignsSubtitle'),
            }
        }

        return {
            title: t('emptyState.noClosedCampaignsTitle'),
            body: t('emptyState.noClosedCampaignsSubtitle'),
        }
    })()

    return (
        <SubmissionsWrapper data-testid="submission-summary-container">
            {isInternalUser && (
                <TeamTypeChipFilter
                    selected={teamTypeFilters?.submission_list?.selected || []}
                    isOpen={teamTypeFilters?.submission_list?.isOpen}
                    onSelectFilter={teamType =>
                        dispatch(
                            setTeamTypeFilters({
                                submission_list: {
                                    selected: teamType,
                                    isOpen: true,
                                },
                            }),
                        )
                    }
                    onDeleteChip={() => {
                        dispatch(clearTeamTypeFilters())
                    }}
                />
            )}
            {shouldDisplay ? (
                <SubmissionsLayout>
                    {submissionsSummary.map((campaign: SubmissionSummaryState) => (
                        <SubmissionsSummaryCard key={campaign.uuid} campaign={campaign} />
                    ))}
                </SubmissionsLayout>
            ) : (
                <EmptyState
                    isLoading={isLoading}
                    width="100%"
                    title={emptyStateValue.title}
                    body={emptyStateValue.body}
                    titleColor="primary"
                    bodyColor="primary"
                    customIcon={emptyStateValue.icon}
                />
            )}

            {isLoading && <StyledLoading />}
        </SubmissionsWrapper>
    )
}

const SubmissionsWrapper = styled.div`
    box-sizing: border-box;
    max-width: 1920px;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[300]};
    margin-top: ${({ theme }) => theme.spacing[400]};
`

const SubmissionsSummary: React.FC = () => {
    const [t] = useNewTranslation()
    const getSubmissionsSummary = useSelector(createGetSubmissionsSummary)
    const isInternalUser = useSelector(selectIsInternalUser)
    const isLoading = useSelector((state: State) => createGetRequestsAreLoading([STATE_KEYS.SubmissionsSummary])(state))

    const [viewType, setViewType] = useState<SubmissionsSummaryViews>('active')
    const [searchTerm, setSearch] = useState<string>('')

    const clearSearch = () => setSearch('')
    const handleSwitchViewType = (type: SubmissionsSummaryViews) => {
        clearSearch()
        setViewType(type)
    }
    const handleClickSearch = (value: string) => {
        setSearch(value)
    }
    return (
        <InnerContainer id="inner-content">
            <Text scale="H4" color="primary">
                {t('submission.allSubmission.allSubmissionPageTitle')}
            </Text>
            <Tab
                tabItemAlign="left"
                selectedTab={viewType}
                tabs={[
                    {
                        id: 'active',
                        label: t('allCampaigns.liveCampaignTab'),
                        onClick: () => handleSwitchViewType('active'),
                    },
                    {
                        id: 'closed',
                        label: t('allCampaigns.closedCampaignTab'),
                        isError: false,
                        disabled: false,
                        onClick: () => handleSwitchViewType('closed'),
                    },
                ]}
            />
            <SecondHeader justifyContent="space-between">
                {/* empty div to make search always right align */}
                {isInternalUser ? <TeamTypeFilter page="submission_list" /> : <div />}
                <Search
                    width="360px"
                    searchValue={searchTerm}
                    onSearch={handleClickSearch}
                    onClearSearch={clearSearch}
                    placeholder={t('placeholderText.enterCampaignBrandName')}
                />
            </SecondHeader>

            <Submissions
                submissionsSummary={getSubmissionsSummary(viewType)}
                isLoading={isLoading}
                viewType={viewType}
                searchTerm={searchTerm}
                handleClearSearchTerm={clearSearch}
            />
        </InnerContainer>
    )
}

export default SubmissionsSummary
